import { useState, Fragment, useCallback, useMemo, useEffect } from 'react'
import { Grid, Button, Stack, Typography } from '@vapor/material'
import { FormHelperText, TextField } from '@mui/material'
import { ResourceOccupancySchema } from '../edit-rr.schema'
import { DatePicker } from '@mui/x-date-pickers-pro'
import { compareDates, addDays, isDateBetween } from 'src/utils/date'
import { useHotelDate } from 'src/app/hooks/context/context.hook'
import { HicIconButton } from 'src/app/components/hic-vapor/button/icon-button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash, faSwapArrows } from '@fortawesome/pro-regular-svg-icons'

import { useTranslation } from 'react-i18next'
import { HicFormLabel } from 'src/app/components/form-fields/hic-form-label.styled'
import ResourceTypeResourceSelect from 'src/app/components/resource-type-resource-select/resource-type-resource-select'
import { ResType, Res, ResourceTypeResource } from 'src/app/types/resources.types'
import useInfoSnackBar from 'src/app/hooks/info-snack-bar/use-info-snack-bar'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { ResourceOccupanciesError } from '../edit-rr.types'

type ResourceOccupanciesProps = {
  value: ResourceOccupancySchema[]
  resources: ResourceTypeResource[]
  resourceTypes: ResType[]
  assignableResources: Res[]
  onChange: (changedValue: ResourceOccupancySchema[]) => void
  errors?: ResourceOccupanciesError[]
}

export const ResourceOccupancies = (props: ResourceOccupanciesProps) => {
  const { value, resources, resourceTypes, assignableResources, onChange, errors } = props
  const { t } = useTranslation()
  const hotelDate = useHotelDate()
  const showInfo = useInfoSnackBar()
  const formatDate = useFormatDate()

  const [localValue, setLocalValue] = useState<ResourceOccupancySchema[]>([])
  const [transferDisabled, setTransferDisabled] = useState<boolean>(false)

  useEffect(() => {
    if (JSON.stringify(localValue) !== JSON.stringify(value)) setLocalValue(value)
    const lastElement = [...value].pop()
    setTransferDisabled(!lastElement || lastElement?.phase === 'CHECKOUT' || compareDates(lastElement?.start, hotelDate) === 0)
  }, [value])

  const defaultNewStartDate = useMemo(() => {
    if (localValue.length > 0) {
      const originalOccupancy = localValue[0]
      if (!isDateBetween(hotelDate, originalOccupancy.start, originalOccupancy.end)) {
        return originalOccupancy.start
      }
    }
    return hotelDate
  }, [hotelDate, localValue])

  const addOccupancyRow = useCallback(() => {
    const lastRow = { ...localValue[localValue.length - 1] }
    let newStart = addDays(new Date(lastRow.start), 1)
    if (compareDates(newStart, lastRow.end) < 0) {
      if (isDateBetween(hotelDate, lastRow.start, lastRow.end)) newStart = hotelDate
      const newRow: ResourceOccupancySchema = {
        start: newStart,
        end: lastRow.end,
        reservedResourceTypeId: lastRow.reservedResourceTypeId,
        assignedResourceTypeId: lastRow.assignedResourceTypeId,
        phase: lastRow.phase,
      }
      setLocalValue((rows) => [...rows.map((r, i, items) => (i === items.length - 1 ? { ...r, end: newStart } : { ...r })), newRow])
    } else {
      showInfo(t('pms.booking.reservation.reserved_resource_edit.error.no_residual_nights_for_transfer'))
    }
  }, [localValue, defaultNewStartDate])

  const removeOccupancyRow = (index: number) => {
    setLocalValue((lv) => {
      const updLv: ResourceOccupancySchema[] = []
      const itemToRemove = lv[index]
      lv.forEach((v, i) => {
        if (i === index - 1) updLv.push({ ...v, end: itemToRemove.end })
        else if (i !== index) updLv.push({ ...v })
      })
      return updLv
    })
  }

  const changeOccupancyRow = (index: number, resourceTypeId?: string, resource?: string) => {
    setLocalValue((lv) => [
      ...lv.map((v, i) => {
        if (i === index && resourceTypeId) {
          return { ...v, assignedResourceTypeId: resourceTypeId, resourceId: resource }
        }
        return { ...v }
      }),
    ])
  }

  // const changeOccupancyStartDateRow = (index: number, newStartDate: Date | null) => {
  //   setLocalValue((lv) => [
  //     ...lv.map((v, i) => {
  //       if (i === index - 1 && newStartDate) {
  //         return { ...v, end: newStartDate }
  //       }
  //       if (i === index && newStartDate) {
  //         return { ...v, start: newStartDate }
  //       }
  //       return { ...v }
  //     }),
  //   ])
  // }

  useEffect(() => {
    onChange(localValue)
  }, [localValue])

  return (
    <Grid container spacing={1}>
      {localValue.length > 1 && (
        <>
          <Grid item xs={3}>
            <HicFormLabel>{t('commons.from_date')}</HicFormLabel>
          </Grid>
          <Grid item xs={4}>
            <HicFormLabel>{t('pms.booking.reservation.reserved_resource_edit.move_into')}</HicFormLabel>
          </Grid>
          <Grid item xs={5}></Grid>
        </>
      )}
      {localValue.map((v, index, items) => {
        const disableEdit = compareDates(v.start, hotelDate) < 0

        const resourceTypeSelected = resourceTypes.find((rt) => rt.resourceTypeId === v.reservedResourceTypeId)
        const assignedResourceTypeSelected = resourceTypes.find((rt) => rt.resourceTypeId === v.assignedResourceTypeId)
        const resourceSeleced = assignableResources.find((r) => r.resourceId === v.resourceId) ?? resources.find((r) => r.resource?.resourceId === v.resourceId)?.resource ?? null
        const resourceTypeResource: ResourceTypeResource | null =
          resourceTypeSelected && resourceSeleced
            ? ({
                resourceType: resourceTypeSelected,
                assignedResourceType: assignedResourceTypeSelected,
                resource: resourceSeleced,
              } as ResourceTypeResource)
            : null

        const prevResourceId = index > 0 ? items[index - 1]?.resourceId : undefined
        const applicableResources = assignableResources.filter((rs) => rs.resourceId !== prevResourceId)

        if (index !== 0)
          return (
            <Fragment key={index}>
              <Grid item xs={3}>
                <Typography variant="body">{formatDate(v.start)}</Typography>
                {/* FOR NOW, THE TRANSFER CAN ONLY TAKE PLACE WITH THE HOTEL DATE. IT IS NOT POSSIBLE TO PLAN FUTURE TRANSFERS*/}
                {/* <DatePicker //
                  value={v.start}
                  onChange={(v, k) => {
                    changeOccupancyStartDateRow(index, v)
                  }}
                  shouldDisableDate={(day) => compareDates(day, prevStart) < 0 || compareDates(day, prevEnd) > 0}
                  renderInput={(params) => (
                    <TextField //
                      fullWidth
                      size="small"
                      {...params}
                    />
                  )}
                /> */}
              </Grid>
              <Grid item xs={3}>
                {disableEdit ? (
                  <TextField //
                    fullWidth
                    value={`${resourceTypeResource?.resource?.code} (${
                      resourceTypeResource?.assignedResourceType ? resourceTypeResource?.assignedResourceType.display : resourceTypeResource?.resourceType.display
                    })`}
                    disabled
                    size="small"
                  />
                ) : (
                  <>
                    <ResourceTypeResourceSelect //
                      resourceTypes={resourceTypes}
                      assignableResources={applicableResources}
                      value={resourceTypeResource}
                      onChange={(v) => {
                        changeOccupancyRow(index, v?.resourceType.resourceTypeId, v?.resource?.resourceId)
                      }}
                      disabled={disableEdit}
                      hideResourceTypeOptions
                      error={errors?.[index]?.resourceId?.message ? true : false}
                    />
                    {(errors?.[index]?.resourceId?.message ? true : false) && <FormHelperText error>{errors?.[index]?.resourceId?.message}</FormHelperText>}
                  </>
                )}
              </Grid>
              <Grid item xs={5}>
                {index !== 0 && (
                  <HicIconButton onClick={() => removeOccupancyRow(index)} color="error" size="small" disabled={disableEdit}>
                    <FontAwesomeIcon icon={faTrash} />
                  </HicIconButton>
                )}
              </Grid>
            </Fragment>
          )
        else return <></>
      })}
      <Grid item xs={6} display={'flex'} justifyContent={'flex-end'}>
        <Button variant="text" onClick={() => addOccupancyRow()} disabled={transferDisabled}>
          <Stack direction="row" spacing={1}>
            <FontAwesomeIcon icon={faSwapArrows} />
            <span>{t('commons.move')}</span>
          </Stack>
        </Button>
      </Grid>
      <Grid item xs={6}></Grid>

      {/* DEBUG ----------------------------------------------------------- */}
      {/* <Grid item xs={12}>
        <div style={{ fontSize: 10, backgroundColor: '#00000011' }}>
          <div style={{ color: 'green' }}>
            <b>D E B U G - resource occupancies</b>
            {localValue.map((i) => (
              <div>
                <span>
                  start: {formatDate(i.start)} - end: {formatDate(i.end)} - resourceId: {i.resourceId}
                </span>
              </div>
            ))}
          </div>
          <div style={{ color: 'royalblue' }}>
            {value.map((i) => (
              <div>
                <span>
                  start: {formatDate(i.start)} - end: {formatDate(i.end)} - resourceId: {i.resourceId}
                </span>
              </div>
            ))}
          </div>
          <div style={{ color: 'red' }}>{JSON.stringify(errors)}</div>
        </div>
      </Grid> */}
      {/* DEBUG ----------------------------------------------------------- */}
    </Grid>
  )
}
